/**
 * Auto-checked exercise model used by the lesson player. Book items of many types
 * (content-schema.md) are normalised into three interaction kinds: choose, fill, order.
 */
import type { Item, ItemType, Sentence, Word } from './content.ts';
import { sameAnswer, answerKey } from './answer.ts';
import { seededShuffle } from './random.ts';

export const BLANK = '____';

interface Base {
  id: string;
  instruction: string;
}

export interface ChoiceExercise extends Base {
  kind: 'choice';
  /** Shown above the options; empty for listening tasks. */
  prompt: string;
  /** Pinyin of a single-word prompt (word drills only). */
  promptRu?: string;
  /** Listening task: this text is spoken instead of shown. */
  listen?: string;
  options: string[];
  /** Every option that must be selected; `multi` means more than one may be correct. */
  correct: string[];
  multi: boolean;
}

export interface FillExercise extends Base {
  kind: 'fill';
  /** Prompt containing one BLANK per entry of `blanks`. */
  prompt: string;
  /** Translation of the whole prompt, when it is a sentence from the text (shown on request). */
  promptRu?: string;
  blanks: string[][];
  /** When present the learner picks words from the bank instead of typing. */
  bank: string[] | null;
}

export interface OrderExercise extends Base {
  kind: 'order';
  promptRu: string;
  /** Tiles in display (shuffled) order. */
  tiles: { surface: string }[];
  /** '' for Chinese, ' ' for languages written with spaces. */
  joiner: '' | ' ';
  answer: string;
}

export type AutoExercise = ChoiceExercise | FillExercise | OrderExercise;

const CHOICE_TYPES: ReadonlySet<ItemType> = new Set([
  'multiple_choice',
  'comprehension',
  'choose_both_allowed',
]);
const FILL_TYPES: ReadonlySet<ItemType> = new Set(['fill_blank', 'fill_from_bank', 'fill_table']);

function flatAnswers(item: Item): string[] {
  if (item.answers && item.answers.length > 0) {
    return item.answers.map((a) => (Array.isArray(a) ? a[0] ?? '' : a)).filter(Boolean);
  }
  return item.answer ? [item.answer] : [];
}

function blankAnswers(item: Item, blankCount: number): string[][] {
  const answers = item.answers ?? (item.answer ? [item.answer] : []);
  const perBlank = answers.map((a) => (Array.isArray(a) ? a : [a]));
  return perBlank.length === blankCount ? perBlank : [];
}

function countBlanks(prompt: string): number {
  return prompt.split(BLANK).length - 1;
}

/** Turns one book item into an auto-checked exercise, or null if it needs AI or media. */
export function fromItem(
  item: Item,
  type: ItemType,
  instruction: string,
  bank?: readonly string[],
): AutoExercise | null {
  if (item.autoCheck === false) return null;
  if (CHOICE_TYPES.has(type)) {
    const correct = flatAnswers(item);
    if (!item.options || item.options.length < 2 || correct.length === 0) return null;
    return {
      kind: 'choice',
      id: item.id,
      instruction,
      prompt: item.prompt,
      ...(item.promptRu ? { promptRu: item.promptRu } : {}),
      options: item.options,
      correct,
      multi: type === 'choose_both_allowed',
    };
  }
  if (type === 'choose_position') {
    const letters = ['A', 'B', 'C', 'D'].filter((l) => item.prompt.includes(l));
    if (!item.answer || !item.word || letters.length < 2) return null;
    return {
      kind: 'choice',
      id: item.id,
      instruction: `${instruction} · «${item.word}»`,
      prompt: item.prompt,
      options: letters,
      correct: [item.answer],
      multi: false,
    };
  }
  if (FILL_TYPES.has(type)) {
    const blanks = countBlanks(item.prompt);
    const answers = blankAnswers(item, blanks);
    if (blanks === 0 || answers.length === 0) return null;
    return {
      kind: 'fill',
      id: item.id,
      instruction,
      prompt: item.prompt,
      blanks: answers,
      bank: bank && bank.length > 0 ? [...bank] : null,
    };
  }
  return null;
}

const ORDER_MAX_TILES = 10;
const LATIN_WORD_RE = /[\p{L}\p{N}'’-]+/gu;

/**
 * «Соберите предложение»: Chinese sentences need hand-checked tokens (single characters
 * would make a meaningless puzzle); Latin-script ones are split into words. Too short or too
 * long sentences are skipped.
 */
export function orderFromSentence(sentence: Sentence, instruction: string): OrderExercise | null {
  const chinese = /\p{Script=Han}/u.test(sentence.text);
  let surfaces: string[];
  if (chinese) {
    if (sentence.tokensStatus !== 'done') return null;
    surfaces = sentence.tokens.filter((t) => !t.punct).map((t) => t.surface);
  } else {
    surfaces = sentence.text.match(LATIN_WORD_RE) ?? [];
  }
  if (surfaces.length < 3 || surfaces.length > ORDER_MAX_TILES) return null;
  const joiner = chinese ? '' : ' ';
  return {
    kind: 'order',
    id: `order-${sentence.id}`,
    instruction,
    promptRu: sentence.ru,
    tiles: seededShuffle(
      surfaces.map((surface) => ({ surface })),
      sentence.id,
    ),
    joiner,
    answer: surfaces.join(joiner),
  };
}

// ---- drills generated from the lesson's words ---------------------------------------------

const DISTRACTORS = 3;

/** Up to DISTRACTORS distinct values from `pool` other than `correct`, stable per seed. */
function distractors(pool: readonly string[], correct: string, seed: string): string[] {
  const key = answerKey(correct);
  const unique = [...new Map(pool.filter((v) => v && answerKey(v) !== key).map((v) => [answerKey(v), v])).values()];
  return seededShuffle(unique, seed).slice(0, DISTRACTORS);
}

/** «Что значит 电台?» — the word is shown, its meaning is picked. Null without distractors. */
export function meaningChoice(word: Word, lessonWords: readonly Word[], instruction: string): ChoiceExercise | null {
  const wrong = distractors(lessonWords.map((w) => w.ru), word.ru, `${word.id}:meaning`);
  if (wrong.length < 2) return null;
  return {
    kind: 'choice',
    id: `meaning-${word.id}`,
    instruction,
    prompt: word.word,
    ...(word.pinyin ? { promptRu: word.pinyin } : {}),
    options: seededShuffle([word.ru, ...wrong], `${word.id}:meaning:order`),
    correct: [word.ru],
    multi: false,
  };
}

/** «Как будет „радиостанция“?» — the meaning is shown, the word is picked. */
export function wordChoice(word: Word, lessonWords: readonly Word[], instruction: string): ChoiceExercise | null {
  const wrong = distractors(lessonWords.map((w) => w.word), word.word, `${word.id}:word`);
  if (wrong.length < 2) return null;
  return {
    kind: 'choice',
    id: `word-${word.id}`,
    instruction,
    prompt: word.ru,
    options: seededShuffle([word.word, ...wrong], `${word.id}:word:order`),
    correct: [word.word],
    multi: false,
  };
}

/** Where `needle` first occurs in `text` (case-insensitive outside Chinese), or -1. */
function findWordIn(text: string, needle: string, chinese: boolean): number {
  return chinese ? text.indexOf(needle) : text.toLowerCase().indexOf(needle.toLowerCase());
}

/**
 * A sentence from the text with the word blanked out, picked from a small bank of lesson
 * words. Uses the word's example sentence when it contains the word, else the first one that does.
 */
export function fillFromSentence(
  word: Word,
  sentences: readonly Sentence[],
  lessonWords: readonly Word[],
  chinese: boolean,
  instruction: string,
): FillExercise | null {
  const preferred = sentences.find((s) => s.id === word.exampleSentenceId);
  const sentence = [preferred, ...sentences].find(
    (s): s is Sentence => s !== undefined && findWordIn(s.text, word.word, chinese) !== -1,
  );
  if (!sentence) return null;
  const at = findWordIn(sentence.text, word.word, chinese);
  const surface = sentence.text.slice(at, at + word.word.length);
  const prompt = sentence.text.slice(0, at) + BLANK + sentence.text.slice(at + word.word.length);
  const bank = seededShuffle(
    [surface, ...distractors(lessonWords.map((w) => w.word), surface, `${word.id}:fill`)],
    `${word.id}:fill:order`,
  );
  return {
    kind: 'fill',
    id: `fill-${word.id}`,
    instruction,
    prompt,
    promptRu: sentence.ru,
    blanks: [[surface]],
    bank,
  };
}

/** Other sentences of the text whose length is closest to `target`, for plausible distractors. */
function similarSentences(target: Sentence, pool: readonly Sentence[], count: number, seed: string): Sentence[] {
  const len = Array.from(target.text).length;
  const others = pool.filter((s) => s.id !== target.id && answerKey(s.text) !== answerKey(target.text));
  const closest = [...others].sort(
    (a, b) => Math.abs(Array.from(a.text).length - len) - Math.abs(Array.from(b.text).length - len),
  );
  return seededShuffle(closest.slice(0, count + 2), seed).slice(0, count);
}

/** «Что было дальше?» — a sentence of the story is shown, the one that follows it is picked. */
export function nextSentenceChoice(
  sentences: readonly Sentence[],
  index: number,
  instruction: string,
): ChoiceExercise | null {
  const current = sentences[index];
  const next = sentences[index + 1];
  if (!current || !next) return null;
  const wrong = similarSentences(next, sentences.filter((s) => s.id !== current.id), 2, `${next.id}:next`);
  if (wrong.length < 2) return null;
  return {
    kind: 'choice',
    id: `next-${current.id}`,
    instruction,
    prompt: current.text,
    options: seededShuffle([next.text, ...wrong.map((s) => s.text)], `${next.id}:next:order`),
    correct: [next.text],
    multi: false,
  };
}

/** Listening: the sentence is spoken, the learner picks which sentence of the text it was. */
export function listeningChoice(
  sentence: Sentence,
  sentences: readonly Sentence[],
  instruction: string,
): ChoiceExercise | null {
  const wrong = similarSentences(sentence, sentences, 2, `${sentence.id}:listen`);
  if (wrong.length < 2) return null;
  return {
    kind: 'choice',
    id: `listen-${sentence.id}`,
    instruction,
    prompt: '',
    listen: sentence.text,
    options: seededShuffle([sentence.text, ...wrong.map((s) => s.text)], `${sentence.id}:listen:order`),
    correct: [sentence.text],
    multi: false,
  };
}

// ---- grading ---------------------------------------------------------------------------

export function gradeChoice(ex: ChoiceExercise, selected: readonly string[]): boolean {
  const want = new Set(ex.correct.map(answerKey));
  const got = new Set(selected.map(answerKey));
  return want.size === got.size && [...want].every((a) => got.has(a));
}

export interface FillResult {
  correct: boolean;
  perBlank: boolean[];
}

export function gradeFill(ex: FillExercise, values: readonly string[]): FillResult {
  const perBlank = ex.blanks.map((accepted, i) =>
    accepted.some((a) => sameAnswer(a, values[i] ?? '')),
  );
  return { correct: perBlank.every(Boolean), perBlank };
}

/** Compares the built sentence as text, so equal tiles in a different order still count. */
export function gradeOrder(ex: OrderExercise, built: readonly string[]): boolean {
  return sameAnswer(ex.answer, built.join(ex.joiner));
}

/** The canonical correct answer, for showing in the feedback sheet. */
export function correctAnswerText(ex: AutoExercise): string {
  switch (ex.kind) {
    case 'choice':
      return ex.correct.join(' / ');
    case 'fill':
      return ex.blanks.map((b) => b[0] ?? '').join(' · ');
    case 'order':
      return ex.answer;
  }
}
